import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Button,
  Typography,
  CircularProgress,
  Box,
  Alert,
  TextField,
} from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import "./LoginPage.scss";
import APIService from "../../Api/ApiService";
import { useNavigate, useLocation } from "react-router-dom";
import { BackToAppDeepLink, Routes } from "../../Routes";

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const accessToken = queryParams.get("accessToken");

  // States
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState(false);
  const [deniedEmail, setDeniedEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uid, setUid] = useState("");
  const [password, setPassword] = useState("");

  const validateTokenAndRedirect = async (token: string) => {
    try {
      setIsLoading(true);
      await APIService.ValidateOAuth2Token(token);

      // If successful, go to dashboard
      navigate(Routes.DASHBOARD_PAGE);
    } catch (err: any) {
      console.error("Validation failed", err);
      setDeniedEmail(err?.body?.email || null);
      setValidationError(true);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Effect to handle automatic OAuth2 validation if token is present
   */
  useEffect(() => {
    localStorage.clear(); // Clear any previous session

    if (accessToken) {
      validateTokenAndRedirect(accessToken);
    }
  }, []);

  const onLogin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await APIService.StartUidLogin(uid, password);
      navigate(Routes.DASHBOARD_PAGE);
    } catch (err: any) {
      setError(err.message || "Errore durante il login.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onLogin();
  };

  const handleMobileExit = () => {
    localStorage.clear();
    navigate(BackToAppDeepLink);
  };

  // 1. SHOW ERROR STATE (Permission denied)
  if (validationError) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        textAlign="center"
        p={3}
      >
        <ErrorOutlineIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Accesso Negato
        </Typography>
        <Typography variant="body1" sx={{ maxWidth: 400, mb: deniedEmail ? 2 : 4 }}>
          L'utente selezionato non dispone dei permessi di accesso a questo
          servizio. Se si ritiene che sia un errore, contattare l'assistenza.
        </Typography>
        {deniedEmail && (
          <Box
            sx={{
              maxWidth: 400,
              width: "100%",
              mb: 4,
              px: 2,
              py: 1,
              bgcolor: "error.light",
              color: "error.contrastText",
              borderRadius: 1,
              wordBreak: "break-all",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {deniedEmail}
            </Typography>
          </Box>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handleMobileExit}
          sx={{ px: 4 }}
        >
          Torna all'App
        </Button>
      </Box>
    );
  }

  // 2. SHOW LOADING STATE (Validating token)
  if (accessToken && isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Verifica autorizzazione in corso...
        </Typography>
      </Box>
    );
  }

  // 3. SHOW DEFAULT LOGIN FORM
  return (
    <div className="login-page-root">
      <Card sx={{ maxWidth: 400, width: "100%", mx: 2, p: 2 }}>
        <CardContent>
          <Box className="text-center">
            <img
              src={"piemonte-csi-logo.png"}
              alt="CSI Logo"
              className="login-logo"
            />
            <Typography
              variant="h5"
              component="h1"
              gutterBottom
              color="primary"
            >
              SyMon Monitor Status
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Accedi con uid e password per visualizzare lo stato dei servizi
              Symon.
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <TextField
              label="UID"
              variant="outlined"
              fullWidth
              value={uid}
              onChange={(e) => setUid(e.target.value)}
              sx={{ mb: 2 }}
              disabled={isLoading}
            />
            <TextField
              label="Password"
              type="password"
              variant="outlined"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 3 }}
              disabled={isLoading}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              disabled={isLoading}
              startIcon={
                isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <LoginIcon />
                )
              }
              sx={{ py: 1.5 }}
            >
              {isLoading ? "Autenticazione in corso..." : "Accedi al Portale"}
            </Button>
          </form>

          <Box mt={4} textAlign="center">
            <Typography variant="caption" display="block" color="text.disabled">
              © {new Date().getFullYear()} CSI Piemonte - Tutti i diritti
              riservati
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
